// Independent of A-F CCMR — do not import Ccmr/* or write ccmrIndicators*.

// TEA's "CCMR Outcomes Bonus" workbook (HB3 funding report) carries a single worksheet
// named CCMR_Outcomes_Bonus_<district#> with one row per annual graduate. It is recognized
// here by an OB-exclusive signature so it is never confused with the CCMR Verifier or the
// CCMR Tracker exports: a worksheet name matching /CCMR_Outcomes_Bonus/i (or /outcomes bonus/i)
// AND the four band/criteria headers that only the OB report carries. We never match on the
// generic 'CCMR Met'/'Excluded from Accountability' columns shared with other CCMR files.

// cell.text throws on merged cells whose master value is null, so derive text from
// cell.value instead.
const cellText = (cell) => {
  const value = cell ? cell.value : null;
  if (value == null) {
    return '';
  }
  if (typeof value === 'object') {
    if (Array.isArray(value.richText)) {
      return value.richText.map((part) => part.text).join('');
    }
    if (value.text != null) {
      return String(value.text);
    }
    return '';
  }
  return String(value);
};

// OB-exclusive header signature. All four must be present on a matching sheet for the file to be
// accepted as a valid Outcomes Bonus upload.
const RequiredHeaders = [
  'CCMR OB Criteria Met',
  'Econ Disadv Graduate',
  'Non-Econ Disadv Graduate',
  'Special Ed Graduate',
];

const isOutcomesBonusSheetName = (name) =>
  /CCMR_Outcomes_Bonus/i.test(name || '') || /outcomes\s*bonus/i.test(name || '');

// Grad year FALLBACK from the file name. The TEA OB file is named by DISTRICT, not year
// (CCMR_Outcomes_Bonus_109904), so a naive 4-digit match grabs the district number
// (10[9904] -> "1099"). Only accept a standalone, plausible graduating-class year (20xx,
// not part of a longer digit run); otherwise default to the current year. NOTE: the OB
// upload flow lets the user pick the graduating class explicitly and overrides this — this
// fallback is only used by the generic Assessment Manager path where no year is chosen.
const resolveAssessmentYear = (fileName) => {
  const name = fileName || '';
  const yearMatch = name.match(/(?:^|[^\d])(20[0-3]\d)(?:[^\d]|$)/);
  if (yearMatch) {
    return yearMatch[1];
  }
  return String(new Date().getFullYear());
};

const isCcmrOutcomesBonus = (file) =>
  new Promise((resolve) => {
    const isXlsxFile =
      file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      (file.name || '').toLowerCase().endsWith('.xlsx');
    if (!isXlsxFile) {
      resolve({ isValid: false });
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => resolve({ isValid: false });
    reader.onload = async () => {
      try {
        const exceljsModule = await import('exceljs/dist/exceljs.min.js');
        const ExcelJS = exceljsModule.default || exceljsModule;
        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(reader.result);
        const hasOutcomesBonusSheet = workbook.worksheets.some((sheet) => {
          if (!isOutcomesBonusSheetName(sheet.name)) {
            return false;
          }
          const headerRow = sheet.getRow(1);
          const headers = [];
          for (let col = 1; col <= sheet.columnCount; col += 1) {
            headers.push(cellText(headerRow.getCell(col)).trim());
          }
          return RequiredHeaders.every((header) => headers.includes(header));
        });
        if (hasOutcomesBonusSheet) {
          resolve({ isValid: true, assessmentYear: resolveAssessmentYear(file.name) });
          return;
        }
        resolve({ isValid: false });
      } catch (error) {
        resolve({ isValid: false });
      }
    };
    reader.readAsArrayBuffer(file);
  });

export default isCcmrOutcomesBonus;
